import { useState, useEffect } from "react";
import { clientsEndpoint } from "../constants";

const SmartAlertsNudges = () => {
  const [loading, setLoading] = useState(true);
  const [showNudgeModal, setShowNudgeModal] = useState(false);
  const [nudgeMessage, setNudgeMessage] = useState("");
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [sendingNudge, setSendingNudge] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const [alertsData, setAlertsData] = useState({
    stats: {
      totalAlerts: 45,
      pending: 12,
      resolved: 28,
      critical: 5,
    },
    alerts: [
      {
        id: 1,
        type: "interview_reminder",
        title: "Interview Reminder",
        description: "Sarah Johnson's interview is scheduled for tomorrow at 10:00 AM",
        candidate: { id: 1, name: "Sarah Johnson", position: "UX Designer", avatar: null },
        priority: "high",
        status: "pending",
        createdAt: "2 hours ago",
      },
      {
        id: 2,
        type: "feedback_pending",
        title: "Feedback Pending",
        description: "Feedback for Michael Chen is overdue by 3 days",
        candidate: { id: 2, name: "Michael Chen", position: "Frontend Developer", avatar: null },
        priority: "critical",
        status: "pending",
        createdAt: "3 hours ago",
      },
      {
        id: 3,
        type: "offer_expiring",
        title: "Offer Expiring Soon",
        description: "Offer for Emily Davis expires in 2 days",
        candidate: { id: 3, name: "Emily Davis", position: "Product Manager", avatar: null },
        priority: "medium",
        status: "pending",
        createdAt: "5 hours ago",
      },
      {
        id: 4,
        type: "candidate_inactive",
        title: "Candidate Inactive",
        description: "James Wilson hasn't responded in 7 days",
        candidate: { id: 4, name: "James Wilson", position: "Backend Developer", avatar: null },
        priority: "low",
        status: "resolved",
        createdAt: "1 day ago",
      },
      {
        id: 5,
        type: "application_review",
        title: "Application Review Needed",
        description: "15 new applications waiting for review",
        candidate: null,
        priority: "medium",
        status: "pending",
        createdAt: "1 day ago",
      },
      {
        id: 6,
        type: "interview_feedback",
        title: "Interview Feedback Required",
        description: "Submit feedback for Lisa Anderson's technical interview",
        candidate: { id: 6, name: "Lisa Anderson", position: "Data Scientist", avatar: null },
        priority: "high",
        status: "pending",
        createdAt: "2 days ago",
      },
    ],
  });

  const fetchAlerts = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${clientsEndpoint}alerts/`, {
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (response.ok) {
        const data = await response.json();
        setAlertsData(data);
      }
    } catch (error) {
      console.error("Error fetching alerts:", error);
    }
  };

  const sendNudge = async (method) => {
    setSendingNudge(true);
    try {
      const token = localStorage.getItem("token");
      await fetch(`${clientsEndpoint}nudge/send/`, {
        method: "POST",
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          candidate_id: selectedCandidate?.id,
          message: nudgeMessage,
          method: method,
        }),
      });
      setShowNudgeModal(false);
      setNudgeMessage("");
      setSelectedCandidate(null);
    } catch (error) {
      console.error("Error sending nudge:", error);
    } finally {
      setSendingNudge(false);
    }
  };

  const markAsResolved = async (alertId) => {
    try {
      const token = localStorage.getItem("token");
      await fetch(`${clientsEndpoint}alerts/${alertId}/resolve/`, {
        method: "PATCH",
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
      });
      setAlertsData((prev) => ({
        ...prev,
        alerts: prev.alerts.map((a) =>
          a.id === alertId ? { ...a, status: "resolved" } : a
        ),
      }));
    } catch (error) {
      console.error("Error resolving alert:", error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchAlerts();
      setLoading(false);
    };
    loadData();
  }, []);

  const openNudgeModal = (candidate) => {
    setSelectedCandidate(candidate);
    setNudgeMessage("");
    setShowNudgeModal(true);
  };

  const filteredAlerts = alertsData.alerts.filter((alert) => {
    const matchesTab =
      activeTab === "all" ||
      (activeTab === "pending" && alert.status === "pending") ||
      (activeTab === "resolved" && alert.status === "resolved") ||
      (activeTab === "critical" && alert.priority === "critical");
    const matchesSearch =
      !searchQuery ||
      alert.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      alert.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesTab && matchesSearch;
  });

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "critical":
        return "bg-red-100 text-red-700";
      case "high":
        return "bg-orange-100 text-orange-700";
      case "medium":
        return "bg-yellow-100 text-yellow-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case "interview_reminder":
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        );
      case "feedback_pending":
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
          </svg>
        );
      case "offer_expiring":
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
        );
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#19bfb7]"></div>
          <p className="mt-4 text-gray-600">Loading alerts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Smart Alerts and Nudges</h1>
          <p className="text-sm text-gray-500 mt-1">Stay on top of your recruitment activities</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 mb-1">Total Alerts</p>
              <p className="text-2xl font-semibold text-gray-900">{alertsData.stats.totalAlerts}</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 mb-1">Pending</p>
              <p className="text-2xl font-semibold text-gray-900">{alertsData.stats.pending}</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 mb-1">Resolved</p>
              <p className="text-2xl font-semibold text-gray-900">{alertsData.stats.resolved}</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 mb-1">Critical</p>
              <p className="text-2xl font-semibold text-gray-900">{alertsData.stats.critical}</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
              {[
                { key: "all", label: "All Alerts" },
                { key: "pending", label: "Pending" },
                { key: "resolved", label: "Resolved" },
                { key: "critical", label: "Critical" },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-4 py-1.5 text-sm rounded-md transition-colors ${
                    activeTab === tab.key
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search alerts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#19bfb7] focus:border-transparent w-full md:w-64"
              />
            </div>
          </div>
        </div>

        <div className="divide-y divide-gray-100">
          {filteredAlerts.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-500">No alerts found</p>
            </div>
          ) : (
            filteredAlerts.map((alert) => (
              <div
                key={alert.id}
                className={`p-4 hover:bg-gray-50 transition-colors ${
                  alert.status === "resolved" ? "opacity-60" : ""
                }`}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      alert.priority === "critical"
                        ? "bg-red-100 text-red-600"
                        : alert.priority === "high"
                        ? "bg-orange-100 text-orange-600"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {getAlertIcon(alert.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-gray-900">{alert.title}</h4>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getPriorityColor(alert.priority)}`}>
                            {alert.priority}
                          </span>
                          {alert.status === "resolved" && (
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">
                              Resolved
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{alert.description}</p>
                        {alert.candidate && (
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
                              <span className="text-xs font-medium text-gray-600">
                                {alert.candidate.name.split(" ").map((n) => n[0]).join("")}
                              </span>
                            </div>
                            <span className="text-sm text-gray-600">
                              {alert.candidate.name} - {alert.candidate.position}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-xs text-gray-400">{alert.createdAt}</span>
                        {alert.candidate && alert.status !== "resolved" && (
                          <button
                            onClick={() => openNudgeModal(alert.candidate)}
                            className="px-3 py-1.5 text-sm text-[#19bfb7] border border-[#19bfb7] rounded-lg hover:bg-[#19bfb7] hover:text-white transition-colors"
                          >
                            Nudge
                          </button>
                        )}
                        {alert.status !== "resolved" && (
                          <button
                            onClick={() => markAsResolved(alert.id)}
                            className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                          >
                            Resolve
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {showNudgeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Nudge</h3>
                    <p className="text-sm text-gray-500">Enter your remarks below and send</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowNudgeModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {selectedCandidate && (
                <div className="flex items-center gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                    <span className="text-sm font-medium text-gray-600">
                      {selectedCandidate.name.split(" ").map((n) => n[0]).join("")}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{selectedCandidate.name}</p>
                    <p className="text-xs text-gray-500">{selectedCandidate.position}</p>
                  </div>
                </div>
              )}

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={nudgeMessage}
                  onChange={(e) => setNudgeMessage(e.target.value)}
                  placeholder="Enter your message here..."
                  rows={4}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#19bfb7] focus:border-transparent resize-none"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => sendNudge("in_app")}
                  disabled={!nudgeMessage.trim() || sendingNudge}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-[#19bfb7] border border-[#19bfb7] rounded-lg hover:bg-[#19bfb7] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Send via In-app Message
                </button>
                <button
                  onClick={() => sendNudge("email")}
                  disabled={!nudgeMessage.trim() || sendingNudge}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-[#19bfb7] rounded-lg hover:bg-[#18ada6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Send via Email
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmartAlertsNudges;